import clsx from 'clsx';
import {
  AnimationDefinition,
  Variant,
  easeOut,
  motion,
  useInView,
} from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';

import { matchAnyCharactersOrHtmlTags, removeHtmlTag } from '@constants/regEx';

import { reactNodeParser } from './reactNodeParser';
import './styles.scss';

type AnimatedTextProps = {
  text: string[];
  el?: keyof React.JSX.IntrinsicElements;
  className?: string;
  once?: boolean;
  animation?: {
    hidden: Variant;
    visible: Variant;
  };
  onComplete?: () => void;
  fadeOut?: boolean; // New: allows parent to control fade-out transition
};

const defaultChildAnimation = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.4 } },
};

const getTextClassName = (text: string) => {
  if (text.startsWith('<h4>')) return 'bundleBot_heading';
  if (text.startsWith('<b>')) return 'bundleBot_boldText';
  if (text.startsWith('<a>')) return 'bundleBot_link';
  return 'bundleBot_text';
};

const parsePlainText = (text: string) => {
  if (
    text.startsWith('<h4>') ||
    text.startsWith('<b>') ||
    text.startsWith('<a>')
  ) {
    return removeHtmlTag(text);
  }
  return text;
};

const AnimatedText = ({
  text,
  el: Wrapper = 'span',
  className,
  once,
  animation = defaultChildAnimation,
  onComplete,
  fadeOut = false,
}: AnimatedTextProps) => {
  const [instanceKey, setInstanceKey] = useState(0);
  const prevTextRef = useRef(text.join(''));
  const parentRef = useRef<HTMLElement | null>(null);

  const isInView = useInView(parentRef, { amount: 0.5, once: !!once });

  useEffect(() => {
    const joined = text.join('');
    if (joined !== prevTextRef.current) {
      prevTextRef.current = joined;
      setInstanceKey((k) => k + 1);
    }
  }, [text]);

  const currentAnimate = fadeOut ? 'fadeOut' : isInView ? 'visible' : 'hidden';

  const parentVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.03 } },
    fadeOut: { opacity: 0, transition: { duration: 0.25, ease: easeOut } },
  };

  const handleParentAnimationComplete = (definition: AnimationDefinition) => {
    if (definition === 'visible') {
      onComplete?.();
    }
  };

  const textMap = (textArray: string[]) =>
    textArray.map((sentence, index) => (
      <div key={`${sentence}-${index}`}>
        {sentence
          .split(matchAnyCharactersOrHtmlTags)
          .filter(Boolean)
          .map((char, charIndex) => (
            <motion.span
              key={`${char}-${charIndex}`}
              variants={animation}
              className={getTextClassName(char)}
            >
              {parsePlainText(char)}
            </motion.span>
          ))}
        <span>&nbsp;</span>
      </div>
    ));

  return (
    <Wrapper className={clsx(className, fadeOut && 'animatedText--fadeOut')}>
      <span className="screenReader">{text.join(' ')}</span>

      <motion.span
        key={instanceKey}
        ref={parentRef}
        initial="hidden"
        animate={currentAnimate}
        variants={parentVariants}
        aria-hidden
        onAnimationComplete={handleParentAnimationComplete}
      >
        {textMap(text)}
      </motion.span>
    </Wrapper>
  );
};

export { AnimatedText, reactNodeParser };
